/* eslint-disable no-alert */
/* eslint-disable react/no-unstable-nested-components */
import { ApolloError, useApolloClient } from "@apollo/client";
import { Mutation } from "@apollo/client/react/components";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import FundamenteiWithText from "components/branding/FundamenteiWithText";
import LogoSizingProps from "components/branding/LogoSizingProps";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import { getUtmParametersFromParsedUrlQuery } from "lib/analytics/utm";
import Link from "next/link";
import Router, { useRouter } from "next/router";
import { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Auth from "../components/auth/Auth";
import Footer from "../components/auth/Footer";
import GoogleOAuthButton from "../components/auth/GoogleOAuthButton";
import SignUpForm from "../components/auth/SignUpForm";
import Divider from "../components/core/Divider";
import { useAmplitude } from "../lib/amplitude/Amplitude";
import useFlashMessage from "../lib/hooks/useFlashMessage";
import SignUpFromGoogleOAuthAccessToken from "../lib/queries/auth/SignUpFromGoogleOAuthAccessToken";
import SignUpFromRegistrationForm from "../lib/queries/auth/SignUpFromRegistrationForm";
import type {
  ISignUpFromGoogleOAuthAccessTokenMutation,
  ISignUpFromGoogleOAuthAccessTokenMutationVariables,
} from "../lib/queries/auth/__generated__/SignUpFromGoogleOAuthAccessToken.generated";
import type {
  ISignUpFromRegistrationFormMutation,
  ISignUpFromRegistrationFormMutationVariables,
} from "../lib/queries/auth/__generated__/SignUpFromRegistrationForm.generated";

export default function SignUp() {
  const router = useRouter();
  const intl = useIntl();
  const amplitude = useAmplitude();
  const apolloClient = useApolloClient();
  const [errorMessage, setErrorMessage] = useFlashMessage();
  const sendDataToGTM = useGTMDispatch();

  useEffect(() => {
    apolloClient.resetStore();
  }, []);

  return (
    <div className="mx-auto grid h-screen max-w-7xl grid-cols-3 gap-x-7 md:grid-cols-8 lg:grid-cols-12">
      <FundamenteiMetadata title="Cadastre-se | Fundamentei" />
      <div className="col-span-3 w-full py-10 md:col-span-5 md:col-start-3 lg:col-span-4 lg:col-start-5">
        <div className="grid gap-y-7">
          <div className="text-center">
            <Link href="/" title="Fundamentei" className="flex justify-center text-center">
              <FundamenteiWithText
                {...LogoSizingProps.FundamenteiWithText.MEDIUM}
                style={{ display: "inline-flex !important" }}
              />
            </Link>
          </div>

          <div className="grid gap-y-5 px-6 md:rounded md:bg-white md:p-6 md:shadow-lg">
            <div className="grid gap-y-1 text-center md:text-left">
              <h1 className="m-0 text-xl font-bold tracking-wide text-gray-700">
                <FormattedMessage defaultMessage="Seja bem-vindo!" id="86n5v+" description="Sign up page" />
              </h1>

              <span className="text-sm text-slate-400">
                <FormattedMessage
                  defaultMessage="Forneça alguns dados para criar sua conta."
                  id="zqBBy4"
                  description="Sign up page"
                />
              </span>
            </div>

            <div className="grid gap-y-5">
              <Auth>
                {({ login }) => (
                  <Mutation<ISignUpFromRegistrationFormMutation, ISignUpFromRegistrationFormMutationVariables>
                    mutation={SignUpFromRegistrationForm}
                    onCompleted={(data) => {
                      if (data && data.signUpFromRegistrationForm) {
                        login(data.signUpFromRegistrationForm.accessToken);

                        Router.replace("/");

                        amplitude.logEvent("Signed in", {
                          method: "google",
                          email: data.signUpFromRegistrationForm.email,
                          ...getUtmParametersFromParsedUrlQuery(router.query),
                        });

                        sendDataToGTM({
                          event: "signedUp",
                        });
                      }

                      setErrorMessage("");
                    }}
                  >
                    {(signUpFromRegistrationForm) => (
                      <SignUpForm
                        onSubmit={async ({ firstName, lastName, email, password }) => {
                          try {
                            const { data } = await signUpFromRegistrationForm({
                              variables: {
                                registrationForm: {
                                  firstName,
                                  lastName,
                                  email,
                                  password,
                                },
                              },
                            });

                            if (data?.signUpFromRegistrationForm) {
                              amplitude.logEvent("Signed up", {
                                authentication: "credentials",
                                firstName,
                                lastName,
                                email,
                                ...getUtmParametersFromParsedUrlQuery(router.query),
                              });

                              return true;
                            }

                            alert(
                              intl.formatMessage({
                                defaultMessage:
                                  "Não foi possível processar seu cadastro. Uma conta com esse mesmo email já existe.",
                                id: "qFXy2N",
                                description: "Sign up page",
                              }),
                            );

                            return false;
                          } catch (err) {
                            if (err instanceof ApolloError) {
                              const topError = (err as ApolloError).graphQLErrors[0];

                              if (
                                topError &&
                                (topError.extensions as any) &&
                                (topError.extensions as any).code === "BAD_USER_INPUT"
                              ) {
                                alert(
                                  intl.formatMessage({
                                    defaultMessage:
                                      "Não foi possível processar o cadastro. Verifique os dados do formulário",
                                    id: "5wd1Lo",
                                    description: "Sign up page",
                                  }),
                                );
                              }
                            } else {
                              alert(
                                intl.formatMessage({
                                  defaultMessage: "Whoopz...erro interno do servidor. Tente novamente mais tarde",
                                  id: "SIeI14",
                                  description: "Sign up page",
                                }),
                              );
                            }

                            return false;
                          }
                        }}
                      />
                    )}
                  </Mutation>
                )}
              </Auth>

              {errorMessage && (
                <div className="-mx-6 bg-red-100 px-4 py-3">
                  <p className="m-0 text-center text-sm font-medium tracking-wide text-red-700">{errorMessage}</p>
                </div>
              )}

              <p className="m-0 text-center text-sm text-slate-500">
                <FormattedMessage
                  defaultMessage="Já tem uma conta? <link>Faça login</link>"
                  id="YeuG3E"
                  description="Sign up page"
                  values={{
                    link: (text) => (
                      <Link
                        href="/login"
                        passHref={true}
                        className="ml-auto text-sm font-medium text-blue-600 no-underline hover:underline"
                      >
                        {text}
                      </Link>
                    ),
                  }}
                />
              </p>

              <Divider>
                <span className="text-sm uppercase tracking-wide text-slate-400">
                  <FormattedMessage defaultMessage="OU" id="zIwCPg" description="Sign up page" />
                </span>
              </Divider>

              <Auth>
                {({ login }) => (
                  <Mutation<
                    ISignUpFromGoogleOAuthAccessTokenMutation,
                    ISignUpFromGoogleOAuthAccessTokenMutationVariables
                  >
                    mutation={SignUpFromGoogleOAuthAccessToken}
                    onError={({ networkError, graphQLErrors }) => {
                      if (networkError) {
                        setErrorMessage(
                          intl.formatMessage({
                            defaultMessage: "Não foi possível se comunicar com o servidor.",
                            id: "LZN8Qx",
                            description: "Sign up page",
                          }),
                        );
                        return;
                      }

                      if (graphQLErrors && graphQLErrors.length > 0) {
                        const topError = graphQLErrors[0];

                        if (/Couldn't process your sign up/i.test(topError.message)) {
                          setErrorMessage(
                            intl.formatMessage({
                              defaultMessage:
                                "Não foi possível processar seu cadastro agora. Tente novamente mais tarde. Desculpe pela inconveniência :(",
                              id: "eENysx",
                              description: "Sign up page",
                            }),
                          );
                          return;
                        }
                      }

                      setErrorMessage(
                        intl.formatMessage({
                          defaultMessage: "Whoopz...erro interno do servidor. Tente novamente mais tarde.",
                          id: "8ygmwD",
                          description: "Sign up page",
                        }),
                      );
                    }}
                    onCompleted={(data) => {
                      if (data && data.signUpFromGoogleOAuthAccessToken) {
                        if (data.signUpFromGoogleOAuthAccessToken.isRecentlyCreatedUser) {
                          amplitude.logEvent("Signed up", {
                            authentication: "google",
                            email: data.signUpFromGoogleOAuthAccessToken.email,
                            ...getUtmParametersFromParsedUrlQuery(router.query),
                          });

                          sendDataToGTM({
                            event: "signedUp",
                          });
                        }

                        login(data.signUpFromGoogleOAuthAccessToken.accessToken);

                        Router.replace("/");

                        amplitude.logEvent("Signed in", {
                          method: "google",
                          email: data.signUpFromGoogleOAuthAccessToken.email,
                          ...getUtmParametersFromParsedUrlQuery(router.query),
                        });
                      }

                      setErrorMessage("");
                    }}
                  >
                    {(signUpFromGoogleOAuthAccessToken, { loading }) => {
                      return loading ? (
                        <div className="-mx-6 select-none bg-blue-50 px-6 py-3">
                          <p className="m-0 text-center text-xs font-medium tracking-wider text-blue-600">
                            <FormattedMessage defaultMessage="Aguarde...finalizando seu cadastro." id="iO85uZ" />
                          </p>
                        </div>
                      ) : (
                        <GoogleOAuthButton
                          onGetAccessToken={(accessToken) => {
                            setErrorMessage("");

                            signUpFromGoogleOAuthAccessToken({
                              variables: {
                                accessToken,
                              },
                            });
                          }}
                        >
                          {intl.formatMessage({
                            defaultMessage: "Cadastrar com o Google",
                            id: "+H6dvc",
                            description: "Sign up page",
                          })}
                        </GoogleOAuthButton>
                      );
                    }}
                  </Mutation>
                )}
              </Auth>
            </div>
          </div>

          <Footer />
        </div>
      </div>
    </div>
  );
}
